import React, {ReactElement, useEffect, useState} from 'react';
import {
    AppBar,
    Box,
    Button,
    Card,
    CardMedia,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Rating,
    Select,
    Stack,
    TextField,
    Toolbar,
    Typography,
    useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import StarIcon from '@mui/icons-material/Star';
import {useMutation} from 'react-query';
import {
    AnyQuestion,
    Form,
    FormAnswer,
    FormSubmission,
    LegalQuestion,
    QuestionType,
    ShortTextQuestion
} from "crqlar-react-common/dist/models/forms/form";
import useFormSignatures from "../../useFormSignatures";
import {FormService} from "crqlar-react-common/dist/services/forms/form.service";
import {emailValidator, phoneNumberValidator} from "crqlar-react-common/dist/utils/form.util";
import {Media} from "crqlar-react-common/dist/models/common/media.model";
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import {getScaledUrl, MediaSize} from "crqlar-react-common/dist/utils/media.util";
import SignaturePad from "crqlar-react-common/dist/components/SignaturePad";
import FormThemeProvider from "../FormThemeProvider";
import PhoneInputField from "crqlar-react-common/dist/components/PhoneInputField";
import PublicMediaUpload from "crqlar-react-common/dist/components/PublicMediaUpload";
import {useTranslation} from "react-i18next";

interface SimplePreviewProps {
    form: Form;
    onClose?: () => void;
    isPublic?: boolean;
    isEmbedded?: boolean;
}

const SimplePreview: React.FC<SimplePreviewProps> = ({form, onClose, isPublic = false, isEmbedded}) => {
    const {t} = useTranslation();

    console.log(form.theme)

    const theme = useTheme();
    const [urlParams] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const paramsObj: Record<string, string> = {};
        params.forEach((value, key) => {
            paramsObj[key] = value;
        });
        return paramsObj;
    });
    const [emailValue, setEmailValue] = useState(urlParams.email || '');
    const [formAnswers, setFormAnswers] = useState<Map<string, string | string[] | boolean | number | Media | Media[]>>(new Map());
    const [isSubmitted, setIsSubmitted] = useState(false);
    const contentHolderRef = React.useRef<HTMLDivElement>(null);
    const {
        signaturePadRefs,
        signatureEmptyState,
        uploadingSignatures,
        validationErrors,
        setSignatureEmptyState,
        setValidationErrors,
        uploadSignatures,
        isSignatureQuestionValid
    } = useFormSignatures();

    const submitFormMutation = useMutation({
        mutationFn: async (submissionRequest: FormSubmission) => {
            return await FormService.submitForm(submissionRequest);
        },
        onSuccess: () => {
            setIsSubmitted(true);
        },
        onError: (error) => {
            console.error('Form submission failed:', error);
        }
    });

    // Helper function to get question by ID
    const getQuestionById = (questionId: string): AnyQuestion | undefined => {
        return form.questions?.find(q => q.id === questionId);
    };

    // Helper function to get option label by ID
    const getOptionLabel = (question: AnyQuestion, optionId: string): string => {
        if ('options' in question && question.options) {
            const option = question.options.find(opt => opt.id === optionId);
            return option?.label || optionId;
        }
        return optionId;
    };

    const questions = form.questions || [];

    // Find end screen and email collection settings
    const endScreen = questions.find(q => q.type === QuestionType.END_SCREEN);
    const isEmailRequired = endScreen && 'collectEmail' in endScreen && endScreen.collectEmail;
    const isEmailValidForSubmit = !isEmailRequired || (isEmailRequired && !emailValidator(emailValue));

    // Find redirect URL from end screen
    const redirectUrl = endScreen && 'redirectUrl' in endScreen ? endScreen.redirectUrl : undefined;

    // Check if all required fields are filled
    const areAllRequiredFieldsFilled = (): boolean => {
        const requiredQuestions = questions.filter(q =>
            q.required &&
            q.type !== QuestionType.WELCOME &&
            q.type !== QuestionType.END_SCREEN
        );

        for (const question of requiredQuestions) {
            // For LEGAL questions with signature, use the hook's validation
            if (question.type === QuestionType.LEGAL) {
                const legalQ = question as LegalQuestion;
                if (legalQ.requireSignature) {
                    if (!isSignatureQuestionValid(legalQ, question.id)) {
                        return false;
                    }
                } else {
                    // Must be checked (true)
                    const answer = formAnswers.get(question.id);
                    if (answer !== true) {
                        return false;
                    }
                }
                continue;
            }

            const answer = formAnswers.get(question.id);

            // Check if answer exists and is not empty
            if (answer === undefined || answer === null) {
                return false;
            }

            // For string answers (text inputs, dropdowns)
            if (typeof answer === 'string' && answer.trim() === '') {
                return false;
            }

            // For array answers (multiple choice with multiple selection)
            if (Array.isArray(answer) && answer.length === 0) {
                return false;
            }

            // For UPLOAD_MEDIA questions
            if (question.type === QuestionType.UPLOAD_MEDIA) {
                if (Array.isArray(answer)) {
                    return answer.length > 0;
                }
                // Single media upload
                return answer !== undefined && answer !== null;
            }
        }

        return true;
    };

    // Calculate progress based on filled questions
    const calculateProgress = (): number => {
        const answerableQuestions = questions.filter(q =>
            q.type !== QuestionType.WELCOME &&
            q.type !== QuestionType.END_SCREEN
        );

        if (answerableQuestions.length === 0) {
            return 100;
        }

        let filledCount = 0;
        for (const question of answerableQuestions) {
            const answer = formAnswers.get(question.id);

            // Check if question has a valid answer
            if (answer !== undefined && answer !== null) {
                // For string answers
                if (typeof answer === 'string' && answer.trim() !== '') {
                    filledCount++;
                }
                // For boolean answers (YES_NO, LEGAL)
                else if (typeof answer === 'boolean') {
                    filledCount++;
                }
                // For array answers (multiple choice with multiple selection)
                else if (Array.isArray(answer) && answer.length > 0) {
                    filledCount++;
                }
                // For number answers (rating)
                else if (typeof answer === 'number' && answer > 0) {
                    filledCount++;
                }
                // For object answers (Media uploads)
                else if (typeof answer === 'object' && answer !== null) {
                    filledCount++;
                }
            }
        }

        return (filledCount / answerableQuestions.length) * 100;
    };

    const progress = calculateProgress();

    const handleSubmit = async () => {
        // Upload all signatures before submitting
        const {success, mergedAnswers} = await uploadSignatures(questions, formAnswers);

        if (!success) {
            // Validation errors are already set by the hook
            return;
        }

        // Update form answers with uploaded signatures
        setFormAnswers(mergedAnswers);

        // In preview mode (not public), don't actually submit
        if (!isPublic) {
            setIsSubmitted(true);

            if (contentHolderRef.current) {
                contentHolderRef.current.scrollTo({
                    top: -100,
                    behavior: 'smooth'
                });
            }

            return;
        }

        const answers: FormAnswer[] = [];
        let extractedEmail: string | undefined;
        let extractedPhone: string | undefined;

        // Collect all form answers (use mergedAnswers which includes uploaded signatures)
        mergedAnswers.forEach((answer, questionId) => {
            const questionObj = getQuestionById(questionId);
            if (questionObj) {
                let humanReadableAnswer: string | string[] | boolean | number | Media | Media[] = answer;

                // Extract email/phone from purpose fields (first occurrence only)
                if (questionObj.type === QuestionType.SHORT_TEXT) {
                    const shortTextQ = questionObj as ShortTextQuestion;
                    if (shortTextQ.purpose === 'email' && !extractedEmail && typeof answer === 'string') {
                        extractedEmail = answer;
                    } else if (shortTextQ.purpose === 'tel' && !extractedPhone && typeof answer === 'string') {
                        extractedPhone = answer;
                    }
                }

                // Convert option IDs to human-readable labels for choice questions
                if (questionObj.type === QuestionType.MULTIPLE_CHOICE ||
                    questionObj.type === QuestionType.PICTURE_CHOICE ||
                    questionObj.type === QuestionType.DROPDOWN) {
                    if (typeof answer === 'string') {
                        humanReadableAnswer = getOptionLabel(questionObj, answer);
                    } else if (Array.isArray(answer)) {
                        humanReadableAnswer = answer.map(id => getOptionLabel(questionObj, id as string));
                    }
                }
                // For Yes/No questions, keep as boolean
                else if (questionObj.type === QuestionType.YES_NO) {
                    humanReadableAnswer = answer as boolean;
                }
                // For Legal questions, keep as boolean or Media (for signatures)
                else if (questionObj.type === QuestionType.LEGAL) {
                    // Check if it's a Media object (signature) or boolean (checkbox)
                    if (typeof answer === 'object' && answer !== null && 'id' in answer) {
                        humanReadableAnswer = answer as Media;
                    } else {
                        humanReadableAnswer = answer as boolean;
                    }
                }
                // For Rating questions, keep as number
                else if (questionObj.type === QuestionType.RATING) {
                    humanReadableAnswer = answer as number;
                }

                answers.push({
                    questionTitle: questionObj.title,
                    answer: humanReadableAnswer,
                    questionId: questionObj.id,
                    questionDescription: questionObj.description
                });
            }
        });

        // Also include WELCOME screens for the full picture (they don't have user answers)
        (form.questions || []).forEach((question) => {
            if (question.type === QuestionType.WELCOME) {
                answers.unshift({
                    questionTitle: question.title,
                    answer: "-",
                    questionId: question.id,
                    questionDescription: question.description
                });
            }
        });

        // Use EndScreen email if collected, otherwise use extracted email from purpose field
        const finalEmail = (isEmailRequired ? emailValue : undefined) || extractedEmail;

        const submissionRequest: FormSubmission = {
            formId: form.id,
            answers,
            email: finalEmail,
            phone: extractedPhone,
            ...urlParams
        };

        submitFormMutation.mutate(submissionRequest);
    };

    const questionStyle = {
        borderRadius: form.theme?.shape?.borderRadius,
        border: form.theme?.shape?.questionBorderStyle,
        p: form.theme?.shape?.questionBorderStyle || form.theme?.palette?.questionBackground ? 2 : 0,
        background: form.theme?.palette?.questionBackground
    }

    // Render individual question
    const renderQuestion = (question: AnyQuestion, index: number) => {
        switch (question.type) {
            case QuestionType.WELCOME:
                return (
                    <Box
                        key={question.id}
                        sx={{
                            // textAlign: 'center',
                            ...questionStyle
                        }}
                    >
                        <Typography variant="h3" gutterBottom>
                            {question.title}
                        </Typography>
                        {question.description && (
                            <MdRenderer>
                                {question.description}
                            </MdRenderer>
                        )}
                        <Divider sx={{mt: 3}}/>
                    </Box>
                );

            case QuestionType.SHORT_TEXT:
            case QuestionType.LONG_TEXT:
                const multiline = question.type === QuestionType.LONG_TEXT;
                const currentTextValue = formAnswers.get(question.id) as string || '';
                const shortTextQ = question as ShortTextQuestion;
                const inputType = shortTextQ.purpose || 'text';

                // Validation for email/tel
                const showValidationError = currentTextValue && question.required;
                let validationError = '';
                if (showValidationError && inputType === 'email' && !!emailValidator(currentTextValue)) {
                    validationError = t('Please enter a valid email address');
                } else if (showValidationError && inputType === 'tel' && !!phoneNumberValidator(currentTextValue)) {
                    validationError = t('Please enter a valid phone number');
                }

                return (
                    <Box
                        key={question.id}
                        sx={{
                            ...questionStyle
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {index + 1}. {question.title}
                            {question.required && <span style={{color: theme.palette.error.main}}> *</span>}
                        </Typography>
                        {question.description && (
                            <Box sx={{mb: 2}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Box>
                        )}
                        {inputType === 'tel' ? (
                            <PhoneInputField
                                value={currentTextValue}
                                onChange={(phone) => {
                                    const newAnswers = new Map(formAnswers);
                                    newAnswers.set(question.id, phone);
                                    setFormAnswers(newAnswers);
                                }}
                                error={!!validationError}
                                helperText={validationError}
                                placeholder={question.placeholder || t('Phone number')}
                                variant="light"
                            />
                        ) : (
                            <TextField
                                fullWidth
                                multiline={multiline}
                                rows={multiline ? 4 : 1}
                                type={multiline ? undefined : inputType}
                                placeholder={question.placeholder}
                                value={currentTextValue}
                                onChange={(e) => {
                                    const newAnswers = new Map(formAnswers);
                                    newAnswers.set(question.id, e.target.value);
                                    setFormAnswers(newAnswers);
                                }}
                                variant="outlined"
                                error={!!validationError}
                                helperText={validationError}
                            />
                        )}
                    </Box>
                );

            case QuestionType.YES_NO:
                const currentYesNoValue = formAnswers.get(question.id);
                return (
                    <Box
                        key={question.id}
                        sx={{
                            ...questionStyle
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {index + 1}. {question.title}
                            {question.required && <span style={{color: theme.palette.error.main}}> *</span>}
                        </Typography>
                        {question.description && (
                            <Box sx={{mb: 2}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Box>
                        )}
                        <RadioGroup
                            value={currentYesNoValue === undefined ? '' : currentYesNoValue.toString()}
                            onChange={(e) => {
                                const newAnswers = new Map(formAnswers);
                                newAnswers.set(question.id, e.target.value === 'true');
                                setFormAnswers(newAnswers);
                            }}
                        >
                            <FormControlLabel value="true" control={<Radio/>} label={t('Yes')}/>
                            <FormControlLabel value="false" control={<Radio/>} label={t('No')}/>
                        </RadioGroup>
                    </Box>
                );

            case QuestionType.MULTIPLE_CHOICE:
                const currentMultipleChoiceValue = question.allowMultiple
                    ? (formAnswers.get(question.id) as string[] || [])
                    : (formAnswers.get(question.id) as string || '');

                return (
                    <Box
                        key={question.id}
                        sx={{
                            ...questionStyle
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {index + 1}. {question.title}
                            {question.required && <span style={{color: theme.palette.error.main}}> *</span>}
                        </Typography>
                        {question.description && (
                            <Box sx={{mb: 2}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Box>
                        )}

                        {question.allowMultiple ? (
                            <Box>
                                {question.options && question.options.map((option) => (
                                    <FormControlLabel
                                        key={option.id}
                                        control={
                                            <Checkbox
                                                checked={currentMultipleChoiceValue.includes(option.id)}
                                                onChange={(e) => {
                                                    const newAnswers = new Map(formAnswers);
                                                    const current = formAnswers.get(question.id) as string[] || [];
                                                    if (e.target.checked) {
                                                        newAnswers.set(question.id, [...current, option.id]);
                                                    } else {
                                                        newAnswers.set(question.id, current.filter(id => id !== option.id));
                                                    }
                                                    setFormAnswers(newAnswers);
                                                }}
                                            />
                                        }
                                        label={option.label}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <RadioGroup
                                value={currentMultipleChoiceValue}
                                onChange={(e) => {
                                    const newAnswers = new Map(formAnswers);
                                    newAnswers.set(question.id, e.target.value);
                                    setFormAnswers(newAnswers);
                                }}
                            >
                                {question.options && question.options.map((option) => (
                                    <FormControlLabel
                                        key={option.id}
                                        value={option.id}
                                        control={<Radio/>}
                                        label={option.label}
                                    />
                                ))}
                            </RadioGroup>
                        )}
                    </Box>
                );

            case QuestionType.PICTURE_CHOICE:
                const currentPictureChoiceValue = question.allowMultiple
                    ? (formAnswers.get(question.id) as string[] || [])
                    : (formAnswers.get(question.id) as string || '');

                return (
                    <Box
                        key={question.id}
                        sx={{
                            ...questionStyle
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {index + 1}. {question.title}
                            {question.required && <span style={{color: theme.palette.error.main}}> *</span>}
                        </Typography>
                        {question.description && (
                            <Box sx={{mb: 2}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Box>
                        )}
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                            gap: 2,
                            mt: 2
                        }}>
                            {question.options && question.options.map((option) => {
                                const isSelected = question.allowMultiple
                                    ? currentPictureChoiceValue.includes(option.id)
                                    : currentPictureChoiceValue === option.id;

                                return (
                                    <Card
                                        key={option.id}
                                        sx={{
                                            cursor: 'pointer',
                                            border: '2px solid',
                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                            '&:hover': {
                                                borderColor: 'primary.light'
                                            }
                                        }}
                                        onClick={() => {
                                            const newAnswers = new Map(formAnswers);
                                            if (question.allowMultiple) {
                                                const current = formAnswers.get(question.id) as string[] || [];
                                                if (isSelected) {
                                                    newAnswers.set(question.id, current.filter(id => id !== option.id));
                                                } else {
                                                    newAnswers.set(question.id, [...current, option.id]);
                                                }
                                            } else {
                                                newAnswers.set(question.id, option.id);
                                            }
                                            setFormAnswers(newAnswers);
                                        }}
                                    >
                                        {option.imageMedia ? (
                                            <CardMedia
                                                component="img"
                                                height="120"
                                                image={getScaledUrl(option.imageMedia, MediaSize.LARGE_600, MediaSize.LARGE_600)}
                                                alt={option.label}
                                                sx={{objectFit: 'cover'}}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    height: 120,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: 'grey.100'
                                                }}
                                            >
                                                <AddPhotoAlternateIcon sx={{fontSize: 40, color: 'grey.400'}}/>
                                            </Box>
                                        )}
                                        <Box sx={{p: 1, display: 'flex', alignItems: 'center'}}>
                                            {question.allowMultiple ? (
                                                <Checkbox checked={isSelected} readOnly size="small"/>
                                            ) : (
                                                <Radio checked={isSelected} readOnly size="small"/>
                                            )}
                                            <Typography variant="body2" sx={{ml: 0.5}}>
                                                {option.label}
                                            </Typography>
                                        </Box>
                                    </Card>
                                );
                            })}
                        </Box>
                    </Box>
                );

            case QuestionType.DROPDOWN:
                const currentDropdownValue = formAnswers.get(question.id) as string || '';
                return (
                    <Box
                        key={question.id}
                        sx={{
                            ...questionStyle
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {index + 1}. {question.title}
                            {question.required && <span style={{color: theme.palette.error.main}}> *</span>}
                        </Typography>
                        {question.description && (
                            <Box sx={{mb: 2}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Box>
                        )}
                        <FormControl fullWidth>
                            <Select
                                displayEmpty
                                value={currentDropdownValue}
                                onChange={(e) => {
                                    const newAnswers = new Map(formAnswers);
                                    newAnswers.set(question.id, e.target.value as string);
                                    setFormAnswers(newAnswers);
                                }}
                                MenuProps={{
                                    disablePortal: true,
                                    PaperProps: {
                                        sx: {
                                            bgcolor: '#fff',
                                            maxHeight: 300,
                                        },
                                    }
                                }}
                            >
                                <MenuItem value="" disabled>
                                    <em>{t('Select an option')}</em>
                                </MenuItem>
                                {question.options && question.options.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                );

            case QuestionType.LEGAL:
                const legalQuestion = question as LegalQuestion;
                const currentLegalValue = formAnswers.get(question.id);
                const isSignatureQuestion = legalQuestion.requireSignature;

                return (
                    <Box
                        key={question.id}
                        sx={{
                            ...questionStyle
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {index + 1}. {question.title}
                            {question.required && <span style={{color: theme.palette.error.main}}> *</span>}
                        </Typography>
                        {question.description && (
                            <Box sx={{mb: 2}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Box>
                        )}

                        {
                            question.termsText &&
                            <Paper
                                sx={{
                                    p: 2,
                                    // maxHeight: 200,
                                    overflow: 'auto',
                                    borderRadius: 1,
                                    mb: 2,
                                }}
                            >
                                <MdRenderer>
                                    {question.termsText || t('Terms and conditions text.')}
                                </MdRenderer>
                            </Paper>
                        }


                        {isSignatureQuestion ? (
                            <SignaturePad
                                ref={(ref) => {
                                    if (ref) {
                                        signaturePadRefs.current.set(question.id, ref);
                                    }
                                }}
                                questionId={question.id}
                                value={currentLegalValue as Media}
                                onEmptyChange={(isEmpty) => {
                                    setSignatureEmptyState(prev => {
                                        const newMap = new Map(prev);
                                        newMap.set(question.id, isEmpty);
                                        return newMap;
                                    });
                                }}
                                disabled={submitFormMutation.isLoading}
                                isUploading={uploadingSignatures.has(question.id)}
                            />
                        ) : (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={currentLegalValue as boolean || false}
                                        onChange={(e) => {
                                            const newAnswers = new Map(formAnswers);
                                            newAnswers.set(question.id, e.target.checked);
                                            setFormAnswers(newAnswers);
                                        }}
                                    />
                                }
                                label={t('I agree')}
                            />
                        )}
                    </Box>
                );

            case QuestionType.RATING:
                const currentRatingValue = formAnswers.get(question.id) as number || 0;
                const maxRating = question.maxRating || 10;
                return (
                    <Box
                        key={question.id}
                        sx={{
                            ...questionStyle
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {index + 1}. {question.title}
                            {question.required && <span style={{color: theme.palette.error.main}}> *</span>}
                        </Typography>
                        {question.description && (
                            <Box sx={{mb: 2}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Box>
                        )}
                        {question.ratingLabel && (
                            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                {question.ratingLabel}
                            </Typography>
                        )}
                        <Box sx={{display: 'flex', justifyContent: 'flex-start', my: 2}}>
                            {maxRating === 5 ? (
                                <Rating
                                    value={currentRatingValue}
                                    max={5}
                                    sx={{
                                        fontSize: '3rem',
                                        '& .MuiRating-iconFilled': {
                                            color: 'primary.main'
                                        },
                                        '& .MuiRating-iconHover': {
                                            color: 'primary.dark'
                                        }
                                    }}
                                    onChange={(_, newValue) => {
                                        const newAnswers = new Map(formAnswers);
                                        newAnswers.set(question.id, newValue || 0);
                                        setFormAnswers(newAnswers);
                                    }}
                                    emptyIcon={<StarIcon style={{opacity: 0.3}} fontSize="inherit"/>}
                                />
                            ) : (
                                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start'}}>
                                    {Array.from({length: maxRating}, (_, i) => (
                                        <Box
                                            key={i}
                                            onClick={() => {
                                                const newAnswers = new Map(formAnswers);
                                                newAnswers.set(question.id, i + 1);
                                                setFormAnswers(newAnswers);
                                            }}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px solid',
                                                borderColor: currentRatingValue === i + 1 ? 'primary.main' : 'divider',
                                                backgroundColor: currentRatingValue === i + 1 ? 'primary.main' : 'transparent',
                                                color: currentRatingValue === i + 1 ? 'primary.contrastText' : 'text.primary',
                                                borderRadius: 2,
                                                fontSize: '1rem',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    backgroundColor: currentRatingValue === i + 1 ? 'primary.dark' : 'action.hover'
                                                }
                                            }}
                                        >
                                            {i + 1}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                );

            case QuestionType.UPLOAD_MEDIA:
                const currentUploadValue = formAnswers.get(question.id) as Media | Media[] | undefined;
                return (
                    <Box
                        key={question.id}
                        sx={{
                            ...questionStyle
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {index + 1}. {question.title}
                            {question.required && <span style={{color: theme.palette.error.main}}> *</span>}
                        </Typography>
                        {question.description && (
                            <Box sx={{mb: 2}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Box>
                        )}
                        <PublicMediaUpload
                            value={currentUploadValue}
                            onChange={(media) => {
                                const newAnswers = new Map(formAnswers);
                                if (media) {
                                    newAnswers.set(question.id, media);
                                } else {
                                    newAnswers.delete(question.id);
                                }
                                setFormAnswers(newAnswers);
                            }}
                            allowMultiple={question.allowMultiple}
                            allowedTypes={question.allowedTypes}
                            required={question.required}
                        />
                    </Box>
                );

            case QuestionType.END_SCREEN:
                return (
                    <Box
                        key={question.id}
                        sx={{
                            mt: 2,
                            ...questionStyle
                        }}
                    >
                        <Divider sx={{mb: 3}}/>
                        <Typography variant="h3" gutterBottom textAlign="center">
                            {question.title}
                        </Typography>
                        {question.description && (
                            <Box sx={{mb: 3, textAlign: 'center'}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Box>
                        )}

                        {question.collectEmail && (
                            <TextField
                                fullWidth
                                type="email"
                                value={emailValue}
                                onChange={(e) => setEmailValue(e.target.value)}
                                placeholder={question.emailPlaceholder || t('Enter your email address')}
                                sx={{mb: 3, maxWidth: 400, mx: 'auto', display: 'block'}}
                                error={isEmailRequired && emailValue !== '' && !!emailValidator(emailValue)}
                                helperText={isEmailRequired && emailValue !== '' && !!emailValidator(emailValue) ? t('Please enter a valid email address') : ''}
                            />
                        )}

                        <Box sx={{textAlign: 'center'}}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleSubmit}
                                disabled={!isEmailValidForSubmit || !areAllRequiredFieldsFilled() || submitFormMutation.isLoading}
                                sx={{
                                    textTransform: 'none',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    minWidth: 200
                                }}
                            >
                                {submitFormMutation.isLoading ? (
                                    <CircularProgress size={20}/>
                                ) : (
                                    question.buttonText || t('Submit')
                                )}
                            </Button>
                            {!areAllRequiredFieldsFilled() && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 2,
                                        color: 'error.main',
                                        fontWeight: 'medium'
                                    }}
                                >
                                    {t('All fields marked with * are mandatory')}
                                </Typography>
                            )}
                            {!isPublic && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mt: 2,
                                        color: 'text.secondary',
                                        display: 'block',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    {t('This is a preview. The form will not be actually submitted.')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    let content: ReactElement | null = null

    // Show success screen after submission
    if (isSubmitted) {
        const SubmittedScreen = () => {
            const waitSeconds = (endScreen as any)?.redirectWaitSeconds ?? 5;
            const [countdown, setCountdown] = useState(waitSeconds);

            useEffect(() => {
                if (isPublic && redirectUrl) {
                    // Immediate redirect if wait time is 0 or negative
                    if (waitSeconds <= 0) {
                        window.location.href = redirectUrl;
                        return;
                    }

                    const timer = setInterval(() => {
                        setCountdown((prev: number) => {
                            if (prev <= 1) {
                                clearInterval(timer);
                                window.location.href = redirectUrl;
                                return 0;
                            }
                            return prev - 1;
                        });
                    }, 1000);

                    return () => clearInterval(timer);
                }
            }, []);

            return (
                <Box sx={{textAlign: 'center', py: 4}}>
                    <CheckCircleIcon
                        sx={{
                            fontSize: '80px',
                            color: 'success.main',
                            mb: 3
                        }}
                    />
                    <Typography variant="h3" component="h1" sx={{mb: 2}}>
                        {t('Thank You!')}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        {t('Your form has been submitted successfully.')}
                        {isPublic && redirectUrl && waitSeconds > 0 && (
                            <><br/>{t('Redirecting in {{countdown}} seconds...', {countdown})}</>
                        )}
                    </Typography>
                </Box>
            );
        }

        content = <SubmittedScreen/>
    } else {
        content = (
            <>
                {/* Progress Bar */}
                {form.settings?.showProgressBar && (
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 8,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: form.theme?.palette?.primary,
                            }
                        }}
                    />
                )}

                {/* Form Content */}
                <Stack
                    sx={{
                        height: isPublic ? (isEmbedded ? undefined : '100vh') : 'calc(100vh - 64px)',
                        overflowY: 'auto',
                        backgroundColor: isEmbedded ? undefined : theme.palette.grey[50]
                    }}
                >
                    {
                        isEmbedded ?
                            <Stack
                                sx={{
                                    maxWidth: 800,
                                    width: '100%',
                                    mx: 'auto',
                                    gap: form.theme?.shape?.questionSpacing ? `${form.theme?.shape?.questionSpacing}px` : 4,
                                    backgroundColor: 'background.paper'
                                }}
                            >
                                {/* Render all questions */}
                                {questions.map((question, index) => renderQuestion(question, index))}
                            </Stack>
                            :
                            <Paper
                                elevation={1}
                                sx={{
                                    maxWidth: 800,
                                    width: '100%',
                                    mx: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    p: 4,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 2,
                                    gap: form.theme?.shape?.questionSpacing ? `${form.theme?.shape?.questionSpacing}px` : 4
                                }}
                            >
                                {/* Render all questions */}
                                {questions.map((question, index) => renderQuestion(question, index))}
                            </Paper>
                    }
                </Stack>
            </>
        )
    }

    return (
        <Box
            sx={{
                height: isEmbedded ? undefined : '100vh',
                width: isEmbedded ? undefined : '100vw',
                position: isEmbedded ? undefined : 'fixed',
                top: 0,
                left: 0,
                bgcolor: isEmbedded ? undefined : 'background.default',
            }}
            ref={contentHolderRef}
        >
            {!isPublic && !isEmbedded && (
                <AppBar position="static" color="default" elevation={0}>
                    <Toolbar>
                        <Typography variant="h6" sx={{flexGrow: 1}}>
                            {form.title}
                        </Typography>
                        {onClose && (
                            <Button
                                color="error"
                                size={'large'}
                                onClick={onClose} aria-label={t('Close Preview')}
                                startIcon={<CloseIcon/>}
                                variant={'outlined'}
                            >
                                {t('Close Preview')}
                            </Button>
                        )}
                    </Toolbar>
                </AppBar>
            )}

            <FormThemeProvider theme={form.theme}>
                {content}
            </FormThemeProvider>

        </Box>
    );
};

export default SimplePreview;
